import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Label } from "./ui/label";
import { CalendarIcon, Clock, Globe } from "lucide-react";

// Supported timezones
const TIMEZONES = [
  { value: "America/New_York", label: "EST (Eastern)" },
  { value: "America/Chicago", label: "CST (Central)" },
  { value: "America/Denver", label: "MST (Mountain)" },
  { value: "America/Los_Angeles", label: "PST (Pacific)" },
  { value: "UTC", label: "UTC" },
];

// Date range presets
const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_15_days", label: "Last 15 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom Range" },
];

// Get date range based on preset - properly using EST timezone
const getDateRange = (preset, timezone = "America/New_York") => {
  // Get current time in the specified timezone
  const now = new Date();
  
  // For EST (America/New_York), we need to calculate the correct date
  // Get the timezone offset for EST (usually -5 or -4 for DST)
  const estDate = new Date(now.toLocaleString("en-US", { timeZone: timezone }));
  
  switch (preset) {
    case "today":
      const todayStart = new Date(estDate);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(estDate);
      todayEnd.setHours(23, 59, 59, 999);
      return {
        start: todayStart,
        end: todayEnd,
        displayDate: estDate,
      };
    case "yesterday":
      const yesterdayDate = new Date(estDate);
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterdayStart = new Date(yesterdayDate);
      yesterdayStart.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterdayDate);
      yesterdayEnd.setHours(23, 59, 59, 999);
      return {
        start: yesterdayStart,
        end: yesterdayEnd,
        displayDate: yesterdayDate,
      };
    case "this_week":
      const weekStart = new Date(estDate);
      weekStart.setDate(estDate.getDate() - estDate.getDay()); // Sunday
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(estDate);
      weekEnd.setHours(23, 59, 59, 999);
      return {
        start: weekStart,
        end: weekEnd,
      };
    case "last_week":
      const lastWeekEnd = new Date(estDate);
      lastWeekEnd.setDate(estDate.getDate() - estDate.getDay() - 1); // Last Saturday
      lastWeekEnd.setHours(23, 59, 59, 999);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // Last Sunday
      lastWeekStart.setHours(0, 0, 0, 0);
      return {
        start: lastWeekStart,
        end: lastWeekEnd,
      };
    case "last_15_days":
      const fifteenDaysAgo = new Date(estDate);
      fifteenDaysAgo.setDate(estDate.getDate() - 15);
      fifteenDaysAgo.setHours(0, 0, 0, 0);
      const fifteenEnd = new Date(estDate);
      fifteenEnd.setHours(23, 59, 59, 999);
      return {
        start: fifteenDaysAgo,
        end: fifteenEnd,
      };
    case "last_30_days":
      const thirtyDaysAgo = new Date(estDate);
      thirtyDaysAgo.setDate(estDate.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      const thirtyEnd = new Date(estDate);
      thirtyEnd.setHours(23, 59, 59, 999);
      return {
        start: thirtyDaysAgo,
        end: thirtyEnd,
      };
    case "this_month":
      const monthStart = new Date(estDate.getFullYear(), estDate.getMonth(), 1);
      monthStart.setHours(0, 0, 0, 0);
      const monthEnd = new Date(estDate);
      monthEnd.setHours(23, 59, 59, 999);
      return {
        start: monthStart,
        end: monthEnd,
      };
    case "last_month":
      const lastMonthEnd = new Date(estDate.getFullYear(), estDate.getMonth(), 0);
      lastMonthEnd.setHours(23, 59, 59, 999);
      const lastMonthStart = new Date(estDate.getFullYear(), estDate.getMonth() - 1, 1);
      lastMonthStart.setHours(0, 0, 0, 0);
      return {
        start: lastMonthStart,
        end: lastMonthEnd,
      };
    case "last_6_months":
      const sixMonthsAgo = new Date(estDate);
      sixMonthsAgo.setMonth(estDate.getMonth() - 6);
      sixMonthsAgo.setHours(0, 0, 0, 0);
      const sixMonthsEnd = new Date(estDate);
      sixMonthsEnd.setHours(23, 59, 59, 999);
      return {
        start: sixMonthsAgo,
        end: sixMonthsEnd,
      };
    case "last_year":
      const yearAgo = new Date(estDate);
      yearAgo.setFullYear(estDate.getFullYear() - 1);
      yearAgo.setHours(0, 0, 0, 0);
      const yearEnd = new Date(estDate);
      yearEnd.setHours(23, 59, 59, 999);
      return {
        start: yearAgo,
        end: yearEnd,
      };
    default:
      return null;
  }
};

// Format date for API (YYYY-MM-DDTHH:MM:SS) in EST timezone - ISO 8601
const formatForAPI = (date, timezone = "America/New_York") => {
  // Format the date as it is (already in EST)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// Format date for display
const formatForDisplay = (date) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

export default function DateFilter({ onFilterChange, showTimezone = true, compact = false, defaultPreset = "today" }) {
  const [preset, setPreset] = useState(defaultPreset);
  const [timezone, setTimezone] = useState("America/New_York"); // Default to EST
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);

  // Load saved timezone from localStorage - but default to EST
  useEffect(() => {
    const savedTimezone = localStorage.getItem("preferred_timezone");
    if (savedTimezone) {
      setTimezone(savedTimezone);
    } else {
      // Default to EST if no saved preference
      setTimezone("America/New_York");
    }
  }, []);

  // Save timezone to localStorage when changed
  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone);
    localStorage.setItem("preferred_timezone", newTimezone);
  };

  // Apply filter when preset or dates change
  useEffect(() => {
    applyFilter();
  }, [preset, timezone, customStartDate, customEndDate]);

  const applyFilter = () => {
    let dateRange;
    
    if (preset === "custom") {
      if (customStartDate && customEndDate) {
        // Create proper date objects for custom range
        const startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);
        dateRange = {
          start: startDate,
          end: endDate,
        };
      } else {
        return; // Don't apply filter if custom dates are incomplete
      }
    } else {
      dateRange = getDateRange(preset, timezone);
    }

    if (dateRange && onFilterChange) {
      onFilterChange({
        preset,
        timezone,
        startDate: formatForAPI(dateRange.start),
        endDate: formatForAPI(dateRange.end),
        startDateDisplay: formatForDisplay(dateRange.start),
        endDateDisplay: formatForDisplay(dateRange.end),
      });
    }
  };

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value !== "custom") {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  // For "Today" preset, show single date instead of range
  const currentRange = (() => {
    if (preset === "custom" && customStartDate && customEndDate) {
      return `${formatForDisplay(customStartDate)} - ${formatForDisplay(customEndDate)}`;
    }
    if (preset !== "custom") {
      const range = getDateRange(preset, timezone);
      if (range) {
        // For "Today" and "Yesterday", show single date
        if (preset === "today" || preset === "yesterday") {
          return formatForDisplay(range.start);
        }
        return `${formatForDisplay(range.start)} - ${formatForDisplay(range.end)}`;
      }
    }
    return "Select dates";
  })();

  if (compact) {
    return (
      <div className="flex flex-col sm:flex-row gap-3" data-testid="date-filter">
        <Select value={preset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-full sm:w-48" data-testid="date-preset-select">
            <CalendarIcon className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Select date range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {preset === "custom" && (
          <div className="flex gap-2">
            <Popover open={isStartCalendarOpen} onOpenChange={setIsStartCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto" data-testid="custom-start-date">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {customStartDate ? format(customStartDate, "MMM dd, yyyy") : "Start Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customStartDate}
                  onSelect={(date) => {
                    setCustomStartDate(date);
                    setIsStartCalendarOpen(false);
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Popover open={isEndCalendarOpen} onOpenChange={setIsEndCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto" data-testid="custom-end-date">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {customEndDate ? format(customEndDate, "MMM dd, yyyy") : "End Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customEndDate}
                  onSelect={(date) => {
                    setCustomEndDate(date);
                    setIsEndCalendarOpen(false);
                  }}
                  disabled={(date) => date > new Date() || (customStartDate && date < customStartDate)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {showTimezone && (
          <Select value={timezone} onValueChange={handleTimezoneChange}>
            <SelectTrigger className="w-full sm:w-40" data-testid="timezone-select">
              <Globe className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  }

  return (
    <Card data-testid="date-filter">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Date Preset */}
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground mb-2 block">Date Range</Label>
            <Select value={preset} onValueChange={handlePresetChange}>
              <SelectTrigger data-testid="date-preset-select">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range */}
          {preset === "custom" && (
            <>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-2 block">Start Date</Label>
                <Popover open={isStartCalendarOpen} onOpenChange={setIsStartCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="custom-start-date">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {customStartDate ? format(customStartDate, "yyyy-MM-dd") : "Select start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customStartDate}
                      onSelect={(date) => {
                        setCustomStartDate(date);
                        setIsStartCalendarOpen(false);
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-2 block">End Date</Label>
                <Popover open={isEndCalendarOpen} onOpenChange={setIsEndCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="custom-end-date">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {customEndDate ? format(customEndDate, "yyyy-MM-dd") : "Select end date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customEndDate}
                      onSelect={(date) => {
                        setCustomEndDate(date);
                        setIsEndCalendarOpen(false);
                      }}
                      disabled={(date) => date > new Date() || (customStartDate && date < customStartDate)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          {/* Timezone */}
          {showTimezone && (
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-2 block">Timezone</Label>
              <Select value={timezone} onValueChange={handleTimezoneChange}>
                <SelectTrigger data-testid="timezone-select">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Current Range Display */}
          <div className="flex-1 lg:flex-none">
            <Label className="text-xs text-muted-foreground mb-2 block">Current Filter</Label>
            <div className="flex items-center h-10 px-3 text-sm text-muted-foreground bg-muted/50 rounded-md border">
              <Clock className="h-4 w-4 mr-2" />
              <span className="truncate">{currentRange}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Export utility functions for use in other components
export { formatForAPI, formatForDisplay, getDateRange, TIMEZONES, DATE_PRESETS };
